/**
 * One role in the catalogue list.
 *
 * 🚨 A `system: true` preset offers NO Delete button. Plan §9.2 / §11: the
 * backend 403s on deleting a preset, and rendering a button whose only outcome
 * is a 403 trains people to distrust the UI. The same applies to Edit — presets
 * are immutable, so the card offers "View permissions" instead.
 *
 * Pure and presentational so it can be screenshotted in isolation.
 */
import React from 'react';
import { ShieldCheck, Pencil, Trash2, Users, Eye, AlertTriangle } from 'lucide-react';
import { VERBS, getVerbSet } from '../../types/rbac';
import type { RoleWithPermissions } from '../../types/rbac';

interface RoleCardProps {
  role: RoleWithPermissions;
  /** How many members hold this role — blocks deletion when > 0 (plan §9.2). */
  memberCount: number;
  onEdit: (role: RoleWithPermissions) => void;
  onDelete: (role: RoleWithPermissions) => void;
  /** False for non-admins — the card renders read-only. */
  canManage: boolean;
}

const RoleCard: React.FC<RoleCardProps> = ({
  role, memberCount, onEdit, onDelete, canManage,
}) => {
  const grantedSchemas = Object.keys(role.permissions).filter((key) =>
    VERBS.some((v) => getVerbSet(role.permissions, key)[v]),
  );
  const grantsNothing = grantedSchemas.length === 0;
  const isPreset = role.system;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white/60 dark:bg-gray-800/40">
      {/* Stacks at 375px, row from sm up. */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white break-words">
              {role.name}
            </h3>
            {isPreset && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300">
                <ShieldCheck className="w-3 h-3" />
                Preset
              </span>
            )}
          </div>

          {role.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
              {role.description}
            </p>
          )}

          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <Users className="w-3.5 h-3.5" />
              {memberCount} member{memberCount === 1 ? '' : 's'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {grantedSchemas.length} page{grantedSchemas.length === 1 ? '' : 's'}
            </span>
          </div>

          {/* 🚨 An empty-permission role is legal but strands whoever holds it
              on the no-access page everywhere except Settings. Say so on the
              card rather than letting an admin discover it via a support
              ticket. Plan §9.1. */}
          {grantsNothing && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
              <span>
                Grants no pages. Members with only this role will see Settings
                and nothing else.
              </span>
            </p>
          )}
        </div>

        {/* Full-width buttons at 375px. */}
        <div className="flex items-center gap-2 shrink-0">
          {isPreset ? (
            <button
              onClick={() => onEdit(role)}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
              View
            </button>
          ) : canManage ? (
            <>
              <button
                onClick={() => onEdit(role)}
                aria-label={`Edit ${role.name}`}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
              <button
                onClick={() => onDelete(role)}
                aria-label={`Delete ${role.name}`}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <button
              onClick={() => onEdit(role)}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
              View
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleCard;
